import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { FormattedNumber, injectIntl } from "react-intl"
import PaymentAccount from "../../../payment-account/model/paymentaccount"
import Contact from "../../../state/contact/models/contact"
import ContactService from "../../../state/contact/services"
import { APIError, isError } from "../../../state/helpers/Error"
import IntlProps from "../../../state/helpers/IntlProps"
import { PAYMENT_SOURCE, PAYMENT_STATUS, PAYMENT_TYPE } from "../../../state/helpers/Payment"
import { INTEGRATION_KEY } from "../../../state/integration/Constants"
import PaymentIntentService from "../../../state/payment-intent/PaymentIntentService"
import { Metadata } from "../../../state/payment-processor/metadata"
import PaymentProcessorService from "../../../state/payment-processor/paymentProcessorService"
import { Payin, PayinConfig, PayinStatus } from "../../../state/payment-processor/rainforest/Payin"
import Payment from "../../../state/payment/Payment"
import Proposal from "../../../state/proposal/Proposal"
import ProposalService from "../../../state/proposal/proposalService"
import TerminalService from "../../../state/terminal/TerminalService"
import ActionButton from "../../button/actionbutton.component"
import RainforestErrorRefreshButton from "../../credit-card/rf-components/RainforestErrorRefreshButton"
import LoadingSpinner from "../../spinner/loading.component"
import { messages } from "./messages"

type OwnProps = {
    paymentAccount: PaymentAccount,
    isPresenting: boolean,
    terminalId: string | undefined,
    amount: number,
    currency: string,
    disabled: boolean,
    proposalHash: string,
    contactId: string,
    buildMetaData: () => Metadata,
    onPresent: () => void,
    onCancel: () => void,
    onSuccess: () => void,
}

type Interval = ReturnType<typeof setInterval> | null

const RainforestTerminalPayment = (props: OwnProps & IntlProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [payinStatus, setPayinStatus] = useState<string>("")
    const [payinRefusalDesc, setPayinRefusalDesc] = useState<string>("")
    const [payinPollingInterval, setPayinPollingInterval] = useState<Interval>(null)
    const [paymentPollingInterval, setPaymentPollingInterval] = useState<Interval>(null)
    const [error, setError] = useState<boolean>(false)
    const [cardholderName, setCardholderName] = useState<string>("")
    const [saveCard, setSaveCard] = useState<boolean>(false)
    const [tentativeError, setTentativeError] = useState<string>("")

    useEffect(() => {
        getCardholderName()

        return () => {
            setError(false)
            unbindWindowEvent()
            endPolling(paymentPollingInterval)

            if (payinStatus === PayinStatus.PRESENTING) {
                handlePayinCancelled(payinPollingInterval, true)
            }
        }
    }, [])

    const getCardholderName = async () => {
        setIsLoading(true)
        const contact = await ContactService.Get(props.contactId)

        if (contact instanceof Contact) {
            setCardholderName(`${contact.getFirstName()} ${contact.getLastName()}`)
        }

        setIsLoading(false)
    }

    const handlePresentPayment = async () => {
        setIsLoading(true)
        setTentativeError("")
        const payinConfig = await createPayinConfig()

        if (payinConfig && payinConfig.getId()) {
            await PaymentIntentService.createPaymentIntent({
                externalId: payinConfig.getId(),
                paymentProcessorKey: INTEGRATION_KEY.RAINFOREST,
                paymentMethodType: PAYMENT_TYPE.CREDIT_CARD,
                savePaymentMethodOnFile: saveCard,
                accountId: props.buildMetaData().accountId,
            })

            //there's a race condition where the payment intent might not have been indexed in elastic
            setTimeout(async () => {
                const payin = await presentPayinConfig(payinConfig)

                if (payin && payin.getId()) {
                    setError(false)
                    bindWindowEvent()
                    setPayinStatus(PayinStatus.PRESENTING)
                    startPayinPolling(payin.getId())
                    props.onPresent()
                }

                setIsLoading(false)
            }, 500)
        }
    }

    const createPayinConfig = async (): Promise<PayinConfig | null> => {
        const metaData = props.buildMetaData()
        metaData.paymentMethod = PAYMENT_TYPE.CREDIT_CARD
        metaData.savePaymentMethodOnFile = saveCard

        const payinConfig = await PaymentProcessorService.createPayinConfig(
            props.amount,
            props.paymentAccount.getMerchantId(),
            cardholderName,
            metaData,
            INTEGRATION_KEY.RAINFOREST,
        )

        if (isError(payinConfig)) {
            setError(true)
            return null
        }

        return payinConfig
    }

    const documentTypeAttribute = (documentType: string) => {
        switch (documentType) {
        case "invoice":
            return "invoiceId"
        case "quote":
            return "quoteId"
        case "order":
            return "orderId"
        default:
            return ""
        }
    }

    const presentPayinConfig = async (payinConfig: PayinConfig): Promise<Payin | null> => {
        if (!props.terminalId) return null

        const metaData = props.buildMetaData()
        if (!metaData) {
            return null
        }

        const attributeId = documentTypeAttribute(metaData.documentType)
        const payload = {
            "note": metaData.notes,
            "paymentType": PAYMENT_TYPE.CREDIT_CARD,
            "currency": "USD",
            "contactId": metaData.contactId,
            "source": PAYMENT_SOURCE.BACKOFFICE,
            transactions: [
                {
                    "amount": metaData.amount,
                    [attributeId]: metaData.documentId
                }
            ],
            "fingerprintId": metaData.fingerprintId,
            "externalId": payinConfig.getId(),
            "creditCard": {
                locationId: metaData.locationId
            }
        }

        if (metaData?.transactions) {
            payload.transactions = metaData.transactions
        }

        const res = await AppContext.getApiClient().post("/payment-transactions", payload)
        if (res.error) {
            if ((res as APIError).error?.message) {
                const errorMessage = (res as APIError).error?.message
                setTentativeError(errorMessage || "")
            }

            return null
        } else {
            const payin = await PaymentProcessorService.presentPayinConfig(
                payinConfig.getId(),
                props.terminalId,
            )

            if (isError(payin)) {
                setError(true)
                return null
            }

            return payin
        }
    }

    const refreshProposal = () => {
        window.location.reload()
    }

    const fetchProposal = async (): Promise<Proposal | undefined> => {
        const response = await ProposalService.getProposal(props.proposalHash)
        return isError(response) ? undefined : response as Proposal
    }

    const fetchMostRecentPayment = async (): Promise<Payment | undefined> => {
        const proposal = await fetchProposal()

        if (proposal) {
            return proposal.getMostRecentPayment()
        }

        return undefined
    }

    const startPaymentPolling = () => {
        const interval = setInterval(async () => {
            const payment = await fetchMostRecentPayment()

            if (payment && payment.getStatus() === PAYMENT_STATUS.CAPTURED) {
                endPolling(interval)
                props.onSuccess()
            }
        }, 1000)
        setPaymentPollingInterval(interval)
    }

    const startPayinPolling = (payinId: string) => {
        const interval = setInterval(async () => {
            const payin = await PaymentProcessorService.getPayin(
                payinId,
                props.paymentAccount.getMerchantId(),
                INTEGRATION_KEY.RAINFOREST
            )

            if (!isError(payin)) {
                switch (payin.getStatus()) {
                case PayinStatus.APPROVED:
                case PayinStatus.PROCESSING:
                    handlePayinSuccess(payin.getStatus(), interval)
                    unbindWindowEvent()
                    if (props.proposalHash) {
                        startPaymentPolling()
                    } else {
                        props.onSuccess()
                    }
                    break
                case PayinStatus.CANCELED:
                    handlePayinCancelled(interval, false)
                    unbindWindowEvent()
                    break
                case PayinStatus.FAILED:
                    handlePayinFailed(payin.getRefusalDesc(), interval)
                    unbindWindowEvent()
                    break
                }
            }
        }, 1000)
        setPayinPollingInterval(interval)
    }

    const endPolling = (interval: Interval) => {
        if (interval) {
            clearInterval(interval)
        }
    }

    const sendCancelBeacons = () => {
        if (!props.terminalId) return

        PaymentProcessorService.cancelPayinBeacon(props.terminalId)
        TerminalService.updateBeacon(props.terminalId, false)
    }

    const bindWindowEvent = () => {
        window.addEventListener("beforeunload", sendCancelBeacons)
    }

    const unbindWindowEvent = () => {
        window.removeEventListener("beforeunload", sendCancelBeacons)
    }

    const handlePayinSuccess = async (successStatus: PayinStatus, interval: Interval) => {
        endPolling(interval)
        setPayinStatus(successStatus)
        await setTerminalInUse(false)
    }

    const handlePayinCancelled = async (interval: Interval, triggerCancelQuery: boolean) => {
        if (!props.terminalId) return
        setIsLoading(true)

        endPolling(interval)

        if (payinStatus !== PayinStatus.FAILED) {
            setPayinStatus(PayinStatus.CANCELED)
        }

        if (triggerCancelQuery) {
            await PaymentProcessorService.cancelPayin(props.terminalId)
        }

        await setTerminalInUse(false)
        setIsLoading(false)
        props.onCancel()
    }

    const handlePayinFailed = async (refusalDesc: string, interval: Interval) => {
        if (!props.terminalId) return

        endPolling(interval)
        setPayinStatus(PayinStatus.FAILED)
        setPayinRefusalDesc(refusalDesc)
    }

    const setTerminalInUse = async (inUse: boolean) => {
        if (!props.terminalId) return null

        return TerminalService.update(props.terminalId, inUse)
    }

    const getDisplayedMessage = (): React.ReactElement => {
        switch (payinStatus) {
        case PayinStatus.APPROVED:
        case PayinStatus.PROCESSING:
            return <p className="mt-2 text-success">{props.intl.formatMessage(messages.paymentSuccess)}</p>
        case PayinStatus.FAILED:
            return <p className="mt-2 text-danger">
                {props.intl.formatMessage(messages.paymentFailed, { declinedReason: payinRefusalDesc })}
            </p>
        case PayinStatus.PRESENTING:
        default:
            return <p className="mt-2">{props.intl.formatMessage(messages.paymentPresenting)}</p>
        }
    }

    const getActionButtons = (): React.ReactElement => {
        switch (payinStatus) {
        case PayinStatus.APPROVED:
        case PayinStatus.PROCESSING:
            return <LoadingSpinner color="grey" height={20} width={3} className="px-5" />
        case PayinStatus.PRESENTING:
            return (
                <ActionButton
                    className="btn btn-secondary"
                    onClick={() => handlePayinCancelled(payinPollingInterval, true)}
                >
                    {props.intl.formatMessage(messages.cancel)}
                </ActionButton>
            )
        case PayinStatus.FAILED:
            return (
                <>
                    <ActionButton
                        className="btn btn-primary mr-2"
                        onClick={handlePresentPayment}
                        disabled={isLoading}
                    >
                        {props.intl.formatMessage(messages.retry)}
                    </ActionButton>
                    <ActionButton
                        className="btn btn-secondary ml-2"
                        onClick={() => handlePayinCancelled(payinPollingInterval, true)}
                        disabled={isLoading}
                    >
                        {props.intl.formatMessage(messages.cancel)}
                    </ActionButton>
                </>
            )
        default:
            return (
                <ActionButton
                    className="btn btn-secondary"
                    isLoading={true}
                    disabled={true}
                />
            )
        }
    }

    return (
        <>
            {!tentativeError && !props.isPresenting && (
                <div className="w-100 text-right mt-1">
                    <div className="row mb-2">
                        <div className="col-12">
                            <div className="ml-3 custom-control custom-checkbox">
                                <input
                                    id="saveCard"
                                    name="saveCard"
                                    type="checkbox"
                                    className="custom-control-input"
                                    checked={saveCard}
                                    disabled={props.disabled}
                                    onChange={() => setSaveCard(!saveCard)}
                                />
                                <label className="custom-control-label" htmlFor="saveCard">
                                    {props.intl.formatMessage(messages.saveCard)}
                                </label>
                            </div>
                        </div>
                    </div>
                    {payinStatus === PayinStatus.CANCELED && <p className="text-danger">{props.intl.formatMessage(messages.paymentCancelled)}</p>}
                    {error && <p className="text-danger">{props.intl.formatMessage(messages.paymentError)}</p>}
                    <ActionButton
                        className="btn btn-primary"
                        isLoading={isLoading}
                        onClick={handlePresentPayment}
                        disabled={props.disabled || !props.terminalId || isLoading}
                    >
                        {props.intl.formatMessage(messages.pay)}
                        <FormattedNumber
                            value={props.amount}
                            currency={props.currency}
                            style={"currency"}
                        />
                    </ActionButton>
                </div>
            )}
            {props.isPresenting && (
                <div className="w-100 text-center mt-2 mb-2">
                    <img src="/images/credit-card-payment.svg" height="100px" alt="Credit Card" />
                    {getDisplayedMessage()}
                    {getActionButtons()}
                </div>
            )}
            {tentativeError &&
                <RainforestErrorRefreshButton
                    errorMessage={tentativeError}
                    onClick={refreshProposal}
                />
            }
        </>
    )
}

export default injectIntl(RainforestTerminalPayment)


// WEBPACK FOOTER //
// ./src/common/terminal-payment/rainforest/RainforestTerminalPayment.tsx